import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  Typography,
  IconButton,
  Alert,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon, Send as SendIcon } from "@mui/icons-material";

const InviteUserModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("HR Admin");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email) => {
    // Basic Microsoft 365 email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError("Must be a valid Microsoft 365 work email");
    } else {
      setEmailError("");
    }
  };

  const handleSendInvite = () => {
    if (!email) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Must be a valid Microsoft 365 work email");
      return;
    }
    // Handle send invite logic here
    console.log("Sending invite to:", email, "with role:", role);
    handleClose();
  };

  const handleClose = () => {
    setEmail("");
    setRole("HR Admin");
    setEmailError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 20px 25px -5px rgb(0 0 0 / 0.5)"
              : "0 20px 25px -5px rgb(0 0 0 / 0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
        >
          Invite User
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: theme.palette.text.secondary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Email Input */}
        <Box sx={{ my: 3 }}>
          <FormLabel
            sx={{
              display: "block",
              mb: 1,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            Work Email Address <span style={{ color: "#dc2626" }}>*</span>
          </FormLabel>
          <TextField
            fullWidth
            placeholder="user@company.com"
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={
              emailError || "Must be a valid Microsoft 365 work email"
            }
            FormHelperTextProps={{
              sx: {
                color: emailError ? "#dc2626" : theme.palette.text.secondary,
                fontSize: "12px",
                mt: 0.5,
                ml: 0,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "& fieldset": {
                  borderRadius: 2,
                  borderColor: theme.palette.divider,
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.text.secondary,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
              "& .MuiInputBase-input": {
                color: theme.palette.text.primary,
              },
              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
            }}
          />
        </Box>

        <FormControl component="fieldset" sx={{ width: "100%", mb: 3 }}>
          <FormLabel
            sx={{
              display: "block",
              mb: 1,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            Role <span style={{ color: "#dc2626" }}>*</span>
          </FormLabel>
          <RadioGroup value={role} onChange={(e) => setRole(e.target.value)}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                mb: 1.5,
                backgroundColor:
                  role === "HR Admin"
                    ? theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f9fafb"
                    : theme.palette.background.paper,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f9fafb",
                },
              }}
              onClick={() => setRole("HR Admin")}
            >
              <FormControlLabel
                value="HR Admin"
                control={
                  <Radio
                    sx={{
                      color: theme.palette.text.secondary,
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        fontSize: "15px",
                      }}
                    >
                      HR Admin
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "13px",
                        mt: 0.5,
                      }}
                    >
                      Full access including user management and all interview
                      features
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: "100%" }}
              />
            </Box>

            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                backgroundColor:
                  role === "HR User"
                    ? theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f9fafb"
                    : theme.palette.background.paper,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f9fafb",
                },
              }}
              onClick={() => setRole("HR User")}
            >
              <FormControlLabel
                value="HR User"
                control={
                  <Radio
                    sx={{
                      color: theme.palette.text.secondary,
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        fontSize: "15px",
                      }}
                    >
                      HR User
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "13px",
                        mt: 0.5,
                      }}
                    >
                      Access to dashboard, interviews, and recordings (no user
                      management)
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: "100%" }}
              />
            </Box>
          </RadioGroup>
        </FormControl>

        {/* Info Alert */}
        <Alert
          severity="info"
          icon={false}
          sx={{
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(59, 130, 246, 0.1)"
                : "#eff6ff",
            color:
              theme.palette.mode === "dark"
                ? theme.palette.primary.light
                : "#1e40af",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(59, 130, 246, 0.3)"
                : "#bfdbfe"
            }`,
            "& .MuiAlert-message": {
              fontSize: "13px",
              lineHeight: 1.5,
            },
          }}
        >
          An invitation email will be sent to this address. The user can only
          log in via Microsoft 365 SSO after accepting the invite.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          size="large"
          sx={{
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            "&:hover": {
              borderColor: theme.palette.text.secondary,
            },
          }}
        >
          Cancel
        </Button>

        <Button onClick={handleSendInvite} variant="contained" size="large">
          Send Invite
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUserModal;
